import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Instruction {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export const useInstructions = () => {
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstructions = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('instructions')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setInstructions(data || []);
    } catch (err: any) {
      console.error('Error fetching instructions:', err);
      setError(err.message || 'Błąd podczas pobierania instrukcji');
    } finally {
      setIsLoading(false);
    }
  };

  const createInstruction = async (instruction: Omit<Instruction, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('instructions')
        .insert([instruction])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setInstructions(prev => [data, ...prev]);
      return data;
    } catch (err: any) {
      console.error('Error creating instruction:', err);
      throw err;
    }
  };

  const updateInstruction = async (id: string, updates: Partial<Instruction>) => {
    try {
      const { data, error } = await supabase
        .from('instructions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setInstructions(prev => 
        prev.map(instruction => 
          instruction.id === id ? data : instruction
        )
      );
      return data;
    } catch (err: any) {
      console.error('Error updating instruction:', err);
      throw err;
    }
  };

  const deleteInstruction = async (id: string) => {
    try {
      const { error } = await supabase
        .from('instructions')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setInstructions(prev => prev.filter(instruction => instruction.id !== id));
    } catch (err: any) {
      console.error('Error deleting instruction:', err);
      throw err;
    }
  };

  const getInstructionById = (id: string) => {
    return instructions.find(instruction => instruction.id === id);
  };

  // Automatyczne pobieranie przy pierwszym renderowaniu
  useEffect(() => {
    fetchInstructions();
  }, []);

  return {
    instructions,
    isLoading,
    error,
    fetchInstructions,
    createInstruction,
    updateInstruction,
    deleteInstruction,
    getInstructionById
  };
};
